/**
 * AI CONTEXT
 * Purpose: renders one animated section of AttendanceRow items by category.
 * Inputs: precomputed student rows + section type + status-change handler.
 * Used by: AttendanceViewStudentListContainer.
 * Refactor: unify with TeacherAttendanceSection to reduce duplicated logic.
 */
import { noop } from "lodash"
import { motion } from "motion/react"
import { forwardRef, useState } from "react"

import ComponentErrorBoundary from "#blocks/ComponentErrorBoundary"
import FileDropTarget from "#blocks/FileDropTarget"
import Flex from "#blocks/Flex"
import { CLASS_GROUPS } from "#constants/classOptions"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { type AttendanceRowData } from "#customTypes/composedTypes/classTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import { AttendanceStatus } from "#customTypes/types"
import { getOpenTasks } from "#utils/taskHelpers"
import MissedMakeupSessionSubtitle from "#views/TeacherView/MissedMakeupSessionSubtitle"
import StudentProjectDaySubtitle from "#views/TeacherView/StudentProjectDaySubtitle"

import AttendanceRow from "./AttendanceRow"
import AttendanceSectionHeader from "./AttendanceSectionHeader"

const AttendanceViewStudentListSection = forwardRef<
  HTMLDivElement,
  {
    students: AttendanceRowData[]
    field:
      | "students"
      | "tryouts"
      | "makeups"
      | "assistants"
      | "plannedAbsences"
      | "exceptions"
    session: ClassSessionConstructor
    classGroup: CLASS_GROUPS
    onStatusChange: (
      student: StudentConstructor,
      status: AttendanceStatus,
      sessionId: Uuid,
      projectId?: Uuid
    ) => void
    /**
     * When present, each row becomes an invisible drag-and-drop media target
     * (front desk artwork upload). Row appearance is untouched until a
     * droppable file is dragged over it.
     */
    onMediaDrop?: (student: StudentConstructor, files: File[]) => void
  }
>(
  (
    { students, field, session, classGroup, onStatusChange, onMediaDrop },
    ref
  ) => {
    const [isLoaded, setIsLoaded] = useState(false)

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25, ease: "easeInOut" }}
        onAnimationComplete={() => setIsLoaded(true)}
      >
        {students.length > 0 && (
          <Flex
            col
            style={{
              padding: 6,
            }}
          >
            <AttendanceSectionHeader type={field} count={students.length} />
            {isLoaded &&
              students.map(({ student, status, sessionId, projectId }) => {
                const row = (
                  <AttendanceRow
                    student={student}
                    sessionId={sessionId}
                    status={
                      field === "plannedAbsences"
                        ? ATTENDANCE_STATUSES.PLANNED_ABSENCE
                        : status
                    }
                    classGroup={classGroup}
                    // Tasks are per-student, so the same flag shows on enrolled,
                    // makeup, tryout and assistant rows for that student.
                    openTasks={getOpenTasks(student.tasks)}
                    onStatusChange={
                      field === "plannedAbsences"
                        ? noop
                        : (updatedStudent, updatedStatus) =>
                            onStatusChange(
                              updatedStudent,
                              updatedStatus,
                              sessionId,
                              projectId
                            )
                    }
                    subtitle={
                      field === "makeups" ? (
                        <MissedMakeupSessionSubtitle
                          missedSessionId={sessionId}
                          student={student}
                        />
                      ) : field === "exceptions" ? (
                        <StudentProjectDaySubtitle
                          student={student}
                          session={session}
                          status={status}
                        />
                      ) : undefined
                    }
                  />
                )

                return (
                  <ComponentErrorBoundary
                    key={`${student.id}-${sessionId}-${field}`}
                    variant="line"
                    context={`Student: ${student.name}`}
                  >
                    {onMediaDrop ? (
                      <FileDropTarget
                        testId={`attendance-drop-target-${student.id}`}
                        message="Drop to upload artwork"
                        onDropFiles={(files) => onMediaDrop(student, files)}
                      >
                        {row}
                      </FileDropTarget>
                    ) : (
                      row
                    )}
                  </ComponentErrorBoundary>
                )
              })}
          </Flex>
        )}
      </motion.div>
    )
  }
)

AttendanceViewStudentListSection.displayName =
  "AttendanceViewStudentListSection"

export default AttendanceViewStudentListSection
